/**
 * HubSpot tracking, portal 146774537, EU region.
 *
 * Installed per https://knowledge.hubspot.com/reports/install-the-hubspot-tracking-code,
 * which asks for the snippet before </body>, on every page, exactly once. It
 * is created here rather than pasted into public/index.html for two reasons
 * that both made the pasted version fail validation:
 *
 *  - The page's Content-Security-Policy has no 'unsafe-inline' in script-src,
 *    so the snippet's wrapper never executed. Weakening the policy for one tag
 *    was not worth it. This file ships inside the bundle, which is 'self'.
 *  - doNotTrack has to be pushed BEFORE the loader runs, or HubSpot has
 *    already dropped hubspotutk and __hstc by the time we object. A tag in the
 *    HTML and a push from the bundle race each other; creating the tag after
 *    the push cannot.
 *
 * The loader's own hosts (js-eu1.hs-scripts.com and the three it pulls in
 * turn) are in script-src in public/index.html.
 */

const PORTAL_SRC = '//js-eu1.hs-scripts.com/146774537.js';
const LOADER_ID = 'hs-script-loader';
const CONSENT_KEY = 'lanura_cookie_consent';

/** Only the live site. dev, test and beta share this bundle and would file QA
 *  traffic as real visitors, and HubSpot discards domains absent from its own
 *  settings anyway, so that data would be wrong and incomplete at once. */
const LIVE_HOSTS = new Set(['lanura.ai', 'www.lanura.ai']);

type Hsq = { push: (args: unknown[]) => void };

let historyPatched = false;

const queue = (): Hsq => {
  const w = window as any;
  w._hsq = w._hsq || [];
  return w._hsq as Hsq;
};

/** Report a pageview for the current URL. */
export function trackHubSpotPageView(): void {
  const w = window as any;
  if (!w._hsq) return; // never loaded on this host
  const hsq = queue();
  hsq.push(['setPath', window.location.pathname + window.location.search]);
  hsq.push(['trackPageView']);
}

/** Release tracking at the moment the visitor accepts cookies. */
export function enableHubSpotTracking(): void {
  const w = window as any;
  if (!w._hsq) return;
  const hsq = queue();
  hsq.push(['doNotTrack', { track: true }]);
  // The pageview filed on load was the untracked one, so send it again.
  trackHubSpotPageView();
}

export function initHubSpot(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!LIVE_HOSTS.has(window.location.hostname)) return;
  // The guide is explicit: where two tracking codes load, the second silently
  // never fires. React 18 StrictMode double-invokes effects, so this matters.
  if (document.getElementById(LOADER_ID)) return;

  let consented = false;
  try {
    consented = localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    /* storage blocked: treat as not consented */
  }

  /* Both branches push, and the else branch is the one that was missing.
   *
   * HubSpot remembers doNotTrack in a cookie of its own, which outlives the
   * page and the session. So a visitor whose FIRST visit was before they
   * answered the banner carries that suppression forever: on every later
   * visit this code saw "accepted", pushed nothing, and HubSpot went on
   * honouring the refusal it had stored. Observed on lanura.ai with consent
   * accepted and no __ptq.gif ever firing; pushing track:true by hand made
   * the beacon fire at once.
   *
   * enableHubSpotTracking covers the moment of clicking Accept. This covers
   * every page load after it. */
  const hsq = queue();
  hsq.push(consented ? ['doNotTrack', { track: true }] : ['doNotTrack']);

  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.id = LOADER_ID;
  s.async = true;
  s.defer = true;
  s.src = PORTAL_SRC;
  document.body.appendChild(s);

  // React routing never reloads the document, so without this the loader
  // counts one pageview and then nothing. Same reason PostHog runs with
  // capture_pageview: 'history_change'.
  //
  // Patched at most once. The guard above already stops a second loader, but
  // it keys on an element that something could remove, and wrapping the wrapper
  // would report one route change several times over.
  if (historyPatched) return;
  historyPatched = true;
  const report = () => trackHubSpotPageView();
  // window.history, not the bare global: no-restricted-globals rejects the
  // short form, and it only fires in the production build, so tsc and jest
  // both pass while CI does not.
  const h = window.history;
  (['pushState', 'replaceState'] as const).forEach((m) => {
    const orig = h[m];
    h[m] = function patched(this: History, ...args: any[]) {
      const r = (orig as any).apply(this, args);
      report();
      return r;
    } as any;
  });
  window.addEventListener('popstate', report);
}
